import { combineReducers } from "redux";
import * as Actions from "./actions";

import analysisSetReducer from "./AnalysisSetReducer";
import {
    AnalyseCoreState,
    EditModeEnum,
    LoadingStateEnum,
    PlayStateEnum
} from "./interfaces/state";
import { PeaksJs } from "./interfacesPeaks/PeaksJS";

declare global {
    interface Window {
        _peaksJs: PeaksJs;
    }
}

export function coreReducer(
    state: AnalyseCoreState = null,
    action: Actions.Action
): AnalyseCoreState {
    switch (action.type) {
        case Actions.ActionType.RequestCoughAnalysisSets:
            return {
                ...state,
                loadingState: LoadingStateEnum.AnalysisSets,
            };
        case Actions.ActionType.ReceiveSessionMetadata:
            return {
                ...state,
                sessionMetadata: action.metadata
            };
        case Actions.ActionType.SetStudyId:
            return {
                ...state,
                studyId: action.studyId
            };
        case Actions.ActionType.InitializePeaks:
            // For automated testing purposes, it is useful to have
            // The PeaksJS object available globally to test for correct state.
            if (window) {
                window._peaksJs = action.peaksJs;
            }

            return {
                ...state,
                peaksJs: action.peaksJs
            };
        case Actions.ActionType.DestoryPeaks:

            if (window) {
                window._peaksJs = null;
            }

            return {
                ...state,
                peaksJs: null
            };
        case Actions.ActionType.InitializePeaksEvents:
            return {
                ...state
            };
        case Actions.ActionType.RequestEvents:
            return {
                ...state,
                loadingState: LoadingStateEnum.Events,
            };
        case Actions.ActionType.LoadPositions:
            return {
                ...state,
                positions: action.positions,
            };
        case Actions.ActionType.LoadingPositions:
            return {
                ...state,
                loadingState: LoadingStateEnum.Positions,
            };
        case Actions.ActionType.ReceiveEvents:
            return {
                ...state
            };
        case Actions.ActionType.RequestAnalysisSetComments:
            return {
                ...state,
                comments: action.comments,
            }
        case Actions.ActionType.RequestCurrentAnalysisSet:
            return {
                ...state,
                coughAnalysisSet: action.analysisSet,
            }
        case Actions.ActionType.RequestVisualReviewForAnalysisSet:
            return {
                ...state,
                requiresVisualReview: action.visualReview,
            }
        case Actions.ActionType.RequestBlindComments:
            return {
                ...state,
                blindComments: action.blindComments,
            }
        case Actions.ActionType.RequestEventTypes:
            return {
                ...state,
                loadingState: LoadingStateEnum.EventTypes
            };
        case Actions.ActionType.ReceiveEventTypes:
            return {
                ...state,
                availableEventTypes: action.eventTypes,
                selectedEventType:
                    state.selectedEventType === undefined
                        ? action.eventTypes[0]
                        : state.selectedEventType
            };
        case Actions.ActionType.Play:
            return {
                ...state,
                playState: PlayStateEnum.Playing
            };
        case Actions.ActionType.Pause:
            return {
                ...state,
                playState: PlayStateEnum.Paused
            };
        case Actions.ActionType.ToggleMuteMicrophone:
            return {
                ...state,
                isMicrophoneMuted: !state.isMicrophoneMuted
            };
        case Actions.ActionType.ToggleMuteSensor:
            return {
                ...state,
                isSensorMuted: !state.isSensorMuted
            };
        case Actions.ActionType.ToggleGridline:
            return {
                ...state,
                isGridlineVisible: !state.isGridlineVisible
            };
        case Actions.ActionType.ToggleScrollAction:
            return {
                ...state,
                isScrollActionZoom: !state.isScrollActionZoom
            };
        case Actions.ActionType.SetZoomLevel:
            return {
                ...state,
                zoomLevel: action.level
            };
        case Actions.ActionType.SetWaveform:
            return {
                ...state,
                selectedWaveform: action.waveform
            };
        case Actions.ActionType.ChangeWaveform:
            return {
                ...state,
                previouslySelectedWaveform: action.currentWaveform,
                selectedWaveform: action.waveformToChangeTo,
                previousPlaybackTimepoint: action.currentTime,
                isMicrophoneMuted: false,
                isSensorMuted: false,
            };
        case Actions.ActionType.ChangeSelectedEvent:
            return {
                ...state,
                selectedEventType: action.eventType
            };
        case Actions.ActionType.LoadError:
            return {
                ...state,
                isSubmitting: false,
                isLoadError: true
            };
        case Actions.ActionType.HourlySegmentsLoadError:
            return {
                ...state,
                configErrorMessage: "Failed to load Hour Segments List, this feature has been disabled",
                loadingState: LoadingStateEnum.Loaded,
            };
        // This used to be FinishMovePointEvent, however if an error occurs when updating the point after edit
        // (e.g. the user edited the point to the same position as another point). Then we don't want to close
        // the edit mode. This action is called when the edit is finally complete.
        case Actions.ActionType.ClosePeaksEditMode:
            return {
                ...state,
                isEditMode: EditModeEnum.NotEditing
            };
        case Actions.ActionType.MovePointEvent:
            return {
                ...state,
                selectedEventType: state.availableEventTypes.find(aet => aet.Id === action.event.eventTypeId),
                isEditMode: EditModeEnum.EditingPoint
            };
        case Actions.ActionType.MovePeriodEvent:
            return {
                ...state,
                selectedEventType: state.availableEventTypes.find(aet => aet.Id === action.event.eventTypeId),
                isEditMode: EditModeEnum.EditingPeriod
            };
        case Actions.ActionType.FinishMovePeriodEvent:
            return {
                ...state,
                isEditMode: EditModeEnum.NotEditing
            };
        case Actions.ActionType.RemovePointEvent:
            return {
                ...state,
                isEditMode: EditModeEnum.NotEditing
            };
        case Actions.ActionType.RemovePeriodEvent:
            return {
                ...state,
                isEditMode: EditModeEnum.NotEditing
            };
        case Actions.ActionType.RemoveComment:
            return {
                ...state,
                isEditMode: EditModeEnum.NotEditing
            };
        case Actions.ActionType.OpenFixedCommentsDialog:
            return {
                ...state,
                isFixedCommentsDialogOpen: true
            };
        case Actions.ActionType.CloseFixedCommentsDialog:
            return {
                ...state,
                isFixedCommentsDialogOpen: false
            };
        case Actions.ActionType.ToggleKeyboardShortcuts:
            return {
                ...state,
                areKeyboardShortcutsVisible: !state.areKeyboardShortcutsVisible
            };
        case Actions.ActionType.RequestFixedComments:
            return {
                ...state,
                loadingState: LoadingStateEnum.SessionComments
            };
        case Actions.ActionType.ReceiveFixedComments:
            return {
                ...state,
                availableFixedComments: action.comments.map(comment => ({
                    ...comment,
                    IsSelected: false
                }))
            };
        case Actions.ActionType.UpdateViewOnlyState:
            return {
                ...state,
                isViewOnly: action.isViewOnly
            };
        case Actions.ActionType.UpdateAddEventByDoubleClickState:
            return {
                ...state,
                canAddEventByDoubleClick: action.canAddEventByDoubleClick
            };
        case Actions.ActionType.ResumePlaybackJumpComplete:
            return {
                ...state,
                storedPlaybackTime: null
            };
        case Actions.ActionType.OpenEventListDialog:
            return {
                ...state,
                isEventListOpen: true
            };
        case Actions.ActionType.CloseEventListDialog:
            return {
                ...state,
                isEventListOpen: false
            };
        case Actions.ActionType.CloseSendForVisualReviewDialog:
            return {
                ...state,
                isSendForReviewModalOpen: false
            };
        case Actions.ActionType.ReceiveNotes:
            return {
                ...state,
                sessionNotes: action.notes
            };
        case Actions.ActionType.ToggleSessionNotesVisibility:
            return {
                ...state,
                isSessionNotesVisible: !state.isSessionNotesVisible
            };
        case Actions.ActionType.AddingNewNote:
            return {
                ...state,
                isAddingSessionNote: true
            };
        case Actions.ActionType.AddNewNoteDone:
            return {
                ...state,
                isAddingSessionNote: false
            };
        case Actions.ActionType.PlayheadMapped:
            return {
                ...state,
                loadingState: LoadingStateEnum.Loaded
            };
        case Actions.ActionType.OpenHourSegmentsModal:
            return {
                ...state,
                isHourSegmentsModalOpened: true
            };
        case Actions.ActionType.CloseHourSegmentsModal:
            return {
                ...state,
                isHourSegmentsModalOpened: false
            };
        case Actions.ActionType.LoadHourSegments:
            const kvp = action.hourSegmentsMap.entries().next().value;
            return {
                ...state,
                hourSegmentsMap: state.hourSegmentsMap.set(kvp[0], kvp[1])
            }
        case Actions.ActionType.RequestHourSegments:
            return {
                ...state,
                loadingState: LoadingStateEnum.HourSegments
            };
        case Actions.ActionType.ShowAcceptabilityAuditModal:
            return {
                ...state,
                isAcceptabilityAuditModalVisible: true,
                acceptabilityStatus: action.acceptability,
            };
        case Actions.ActionType.CloseAcceptabilityAuditModal:
            return {
                ...state,
                isAcceptabilityAuditModalVisible: false,
                acceptabilityStatus: null,
            };
        case Actions.ActionType.AcceptabilitySetIsSubmitting:
            return {
                ...state,
                isSubmitting: true,
            };
        case Actions.ActionType.PopulateRawTimepoints:
            return {
                ...state,
                rawTimestampPoints: action.rawTimestampPoints,
            };
        case Actions.ActionType.PlaybackServiceStatusAction:
            return {
                ...state,
                playbackServiceStatus: action.status,
            };
        case Actions.ActionType.EventTypesNotConfigured:
            return {
                ...state,
                isViewOnly: true,
                configErrorMessage: "Failed to load event types, please contact the administrator to configure these for the study",
                loadingState: LoadingStateEnum.Loaded,
            };
        default:
            return state;
    }
}

export default combineReducers({
    core: coreReducer,
    analysisSet: analysisSetReducer
});
